import { ConfirmDialog } from 'primereact/confirmdialog';
import { TabPanel, TabView } from 'primereact/tabview';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import dayjs from 'dayjs';
import { useForm } from '../../../components/Form/hooks';
import {
  TratamentoSementesFormValues,
  TratamentoSementesToSave,
} from '../../../@types';
import { defaultValues, validationSchema } from './form-controller';
import TratamentoDeSementesParametros from './parametros';
import TratamentoDeSementesResumo from './resumo';
import useAsyncFetch from '../../../hooks/use-async';
import { TratamentoSementesService } from '../../../services';
import Agro1Toast from '../../../components/Agro1Toast';
import Agro1Loader from '../../../components/Agro1Loader';
import { beforeExit } from '../../utils';
import { calculaQuantidadeInsumo, isSegundoTratamento } from './utils';

const TratamentoSementesCreateEditPage = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);
  const formObject = useForm<TratamentoSementesFormValues>({
    validationSchema,
    defaultValues,
  });

  const { watch, handleSubmit } = formObject.form;
  const tratamentos = watch('tratamentos');

  const exit = () => {
    navigate('/tratamento-sementes');
  };

  const { data: tratamentoSementes } = useAsyncFetch(
    {
      withLoader: true,
      callback: async () => {
        if (params.id) return TratamentoSementesService.find(params.id);
      },
      errorMessage: 'Falha ao carregar o registro para edição.',
      onError: exit,
    },
    [params.id],
  );

  const originalTratamentos = useMemo(
    () => tratamentoSementes?.tratamentos,
    [tratamentoSementes],
  );

  const getOriginalTratamento = useCallback(
    (identification: string | undefined) => {
      if (!identification) return;
      const parts = identification.split('-');
      const origemSementeId = Number(parts[0]);
      const destinoSementeId = Number(parts[1]);

      return originalTratamentos?.find(
        (tratamento) =>
          tratamento.sementeOrigemId === origemSementeId &&
          tratamento.sementeDestinoId === destinoSementeId,
      );
    },
    [originalTratamentos],
  );

  const onSubmit = async () => {
    const value = formObject.form.getValues();
    const loaderId = Agro1Loader.show();

    try {
      const record: TratamentoSementesToSave = {
        atividadeId: value.atividade!.id,
        propriedadeId: value.propriedade!.id,
        safraId: value.safra!.id,
        tratamentosAttributes: value.tratamentos.map((tratamento) => {
          const original = getOriginalTratamento(tratamento.identification);
          const segundoTratamento = isSegundoTratamento(tratamento);

          return {
            id: original?.id,
            dataFim: dayjs(tratamento.dataFim).format('YYYY-MM-DD'),
            dataInicio: dayjs(tratamento.dataInicio).format('YYYY-MM-DD'),
            insumosAttributes: tratamento.defensivos.map((defensivo) => ({
              insumoId: defensivo.item.id,
              quantidade: segundoTratamento
                ? defensivo.quantidade!
                : calculaQuantidadeInsumo({
                    quantidade: tratamento.quantidade,
                    peso: tratamento.peso,
                    dose: defensivo.dose!,
                  }),
              dose: segundoTratamento ? undefined : defensivo.dose,
            })),
            quantidadeTotalSementes: segundoTratamento
              ? undefined
              : tratamento.quantidade! * tratamento.unidade!.taxa,
            quantidade: segundoTratamento ? undefined : tratamento.quantidade,
            sementeDestinoId: tratamento.sementeDestino!.itemId,
            sementeOrigemId: tratamento.sementeOrigem!.id,
            sementeOrigemTipo: tratamento.sementeOrigem!.tipo,
            unidadeId: segundoTratamento ? undefined : tratamento.unidade?.id,
            pms: segundoTratamento ? undefined : tratamento.pms,
            peso: segundoTratamento ? undefined : tratamento.peso,
          };
        }),
      };

      if (params.id) {
        await TratamentoSementesService.update({ id: params.id, ...record });
      } else {
        await TratamentoSementesService.create(record);
      }

      exit();

      Agro1Toast.show({
        detail: 'Registro salvo com sucesso.',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      Agro1Toast.show({
        detail: `Falha ao ${params.id ? 'atualizar' : 'criar'} o registro.`,
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }
  };

  return (
    <div className="tratamento-sementes-create-edit m-5">
      <ConfirmDialog />
      <h2 className="text-2xl font-light text-gray">Tratamento de Sementes</h2>
      <TabView
        activeIndex={activeTab}
        onTabChange={(event) => setActiveTab(event.index)}
        renderActiveOnly={false}
      >
        <TabPanel header="Parâmetros" headerClassName="normal-font-size">
          <TratamentoDeSementesParametros
            formObject={formObject}
            tratamentoSementes={tratamentoSementes}
          />
        </TabPanel>
        <TabPanel header="Resumo" headerClassName="normal-font-size">
          <TratamentoDeSementesResumo
            formObject={formObject}
            tratamentoSementes={tratamentoSementes}
          />
        </TabPanel>
      </TabView>

      <div className="flex justify-content-between">
        <Button
          label="Voltar"
          className="button-secondary"
          icon="pi pi-arrow-left"
          severity="success"
          onClick={() => beforeExit({ onConfirm: exit })}
        />
        <Button
          label="Gravar"
          className="button-secondary"
          icon="pi pi-check"
          iconPos="right"
          severity="success"
          onClick={() => handleSubmit(onSubmit)()}
          disabled={tratamentos.length === 0}
        />
      </div>
    </div>
  );
};

export default TratamentoSementesCreateEditPage;
